import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { PHONE } from "../../data/site";
import { scrollToId } from "../../hooks/useLenis";

const HERO_IMG = "https://images.unsplash.com/photo-1759134198561-e2041049419c";

const lineParent = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const lineChild = {
    hidden: { y: "110%" },
    show: {
        y: "0%",
        transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
    },
};

const MaskedLine = ({ children, className = "" }) => (
    <span className="block overflow-hidden">
        <motion.span variants={lineChild} className={`block ${className}`}>
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-end overflow-hidden bg-black"
        >
            {/* Parallax image */}
            <motion.div
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 z-0"
            >
                <img
                    src={HERO_IMG}
                    alt="Interiorul modern al salonului GAN BARBERSHOP"
                    className="img-cover"
                    fetchPriority="high"
                />
            </motion.div>

            {/* Gradients */}
            <div className="absolute inset-0 z-10 bg-gradient-to-t from-black via-black/50 to-black/30" />
            <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/70 to-transparent" />

            {/* Content */}
            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-20 mx-auto w-full max-w-7xl px-6 pb-20 pt-32 md:px-12 md:pb-28"
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mb-6"
                >
                    <span className="kicker text-gold">Frizerie Premium · București</span>
                </motion.div>

                <motion.h1
                    variants={lineParent}
                    initial="hidden"
                    animate="show"
                    className="font-display text-[12.5vw] font-semibold leading-[0.9] tracking-tight text-white sm:text-7xl md:text-8xl lg:text-[8.5rem]"
                >
                    <MaskedLine>GAN</MaskedLine>
                    <MaskedLine className="text-gold">BARBERSHOP</MaskedLine>
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 max-w-xl"
                >
                    <p className="font-accent text-xl italic text-neutral-100 md:text-2xl">
                        „Stilul începe cu o tunsoare impecabilă.”
                    </p>
                    <p className="mt-5 font-body text-base leading-relaxed text-neutral-300 md:text-lg">
                        La GAN BARBERSHOP transformăm fiecare vizită într-o experiență.
                        Tunsori moderne, atenție la fiecare detaliu și servicii de cea
                        mai bună calitate.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-4"
                >
                    <a
                        data-testid="hero-call-btn"
                        href={`tel:${PHONE.raw}`}
                        className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 font-body text-sm font-semibold text-black transition-transform duration-300 hover:scale-[0.97] sm:w-auto"
                    >
                        <Phone size={17} strokeWidth={2} />
                        Programează-te telefonic
                    </a>
                    <button
                        data-testid="hero-gallery-btn"
                        onClick={() => scrollToId("galerie")}
                        className="inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-white/25 px-8 py-4 font-body text-sm font-semibold text-white transition-colors duration-300 hover:border-white hover:bg-white/5 sm:w-auto"
                    >
                        Vezi galeria
                    </button>
                </motion.div>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 right-6 z-20 hidden items-center gap-3 md:right-12 md:flex"
            >
                <span className="kicker text-neutral-400">Scroll</span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                    className="text-gold"
                >
                    <ArrowDown size={18} />
                </motion.span>
            </motion.div>
        </section>
    );
};
